import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Users,
  Plus,
  Phone,
  Search,
  X,
  Loader2,
  Flame,
  Clock,
  Zap,
  Sparkles,
  Moon,
  LayoutGrid,
} from 'lucide-react';
import { usePersonStore } from '../store/personStore';
import { useAuthStore } from '../store/authStore';
import { getBookUserId, bookLabel } from '../lib/actingFor';
import { PersonForm } from '../components/people';
import { PersonCard } from '../components/people/PersonCard';
import { SegmentPills } from '../components/shared/SegmentPills';
import { SaveAsQueueModal } from '../components/callQueues';
import type { CustomerStatus, PersonFilters, QueueFilters } from '../types';

const VALID_STATES: CustomerStatus[] = ['PROSPECT', 'ONBOARDED', 'ACTIVE', 'SLIPPING', 'LAPSED'];
const VALID_STAGES = ['LEAD', 'PROSPECT', 'CUSTOMER', 'CHURNED'] as const;
const VALID_TEMPS = ['HOT', 'WARM', 'COLD', 'FREEZING'] as const;

// CRM-35: Call Queues are hidden behind a build-time flag until the feature is
// ready for prime time. Off everywhere by default; matches App.tsx / Navigation.tsx.
const enableCallQueues = import.meta.env.VITE_ENABLE_CALL_QUEUES === 'true';

/**
 * People page — segment pills + card layout
 */
export const People = () => {
  const {
    fetchPeople,
    fetchCounts,
    fetchSegmentCounts,
    people,
    pagination,
    isLoading,
    error,
    clearError,
    counts,
    segment,
    setSegment,
    setFilters,
    setPage,
    filters,
    segmentCounts,
    isLoadingCounts,
  } = usePersonStore();
  const { user: currentUser } = useAuthStore();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [showSaveAsQueue, setShowSaveAsQueue] = useState(false);
  // CRM-35: "Save as Call Queue" is hidden behind the build-time Call Queues
  // flag (off everywhere until the feature is ready), in addition to the role gate.
  const canMakeQueue =
    enableCallQueues && ['INSIDE_SALES', 'MANAGER', 'ADMIN'].includes(currentUser?.role ?? '');
  const [searchInput, setSearchInput] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sportDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const sportFilter = filters.sport ?? '';
  const roleFilter = filters.role ?? '';
  const stateFilter = filters.state ?? [];

  // Local input state for inputs that need controlled display + debounced apply
  const [sportInput, setSportInput] = useState(sportFilter);
  const [roleInput, setRoleInput] = useState(roleFilter);
  const [stateInput, setStateInput] = useState(stateFilter.join(', '));
  const roleDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Advanced filters disclosure: start open only if any advanced filter is set at mount time
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(() =>
    Boolean(
      sportFilter || roleFilter || stateFilter.length > 0 ||
      (filters.customerStatus && filters.customerStatus.length > 0),
    ),
  );

  // Fetch counts + people + segment counts on mount
  useEffect(() => {
    fetchCounts();
    fetchPeople();
    fetchSegmentCounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate customerStatus filter from URL on mount (run once)
  useEffect(() => {
    const param = searchParams.get('customerStatus');
    if (!param) return;
    const parsed = param
      .split(',')
      .filter((s): s is CustomerStatus => VALID_STATES.includes(s as CustomerStatus));
    if (parsed.length > 0) setFilters({ customerStatus: parsed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync customerStatus filter to URL on change (canonical enum-order, replaceState)
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const sorted = VALID_STATES.filter((s) => filters.customerStatus?.includes(s));
    const value = sorted.length > 0 ? sorted.join(',') : null;
    if (value) next.set('customerStatus', value);
    else next.delete('customerStatus');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.customerStatus]);

  // Hydrate sport / role / state + scope / search / stage / temperature from URL
  // (run once on mount). Deep-links (global search "See all results", dashboard
  // tiles & charts) carry these params; the page reads them once and applies them
  // as the initial filters. Also seed local input mirrors so inputs paint the URL
  // value on load. CRM-24.
  useEffect(() => {
    const next: Partial<PersonFilters> = {};
    const sport = searchParams.get('sport');
    if (sport) { next.sport = sport; setSportInput(sport); }
    const role = searchParams.get('role');
    if (role) { next.role = role; setRoleInput(role); }
    const state = searchParams.get('state');
    if (state) {
      const states = state.split(',').filter(Boolean);
      next.state = states;
      setStateInput(states.join(', '));
    }
    // Scope intent: 'all' clears the Mine default; 'mine'/'unassigned' set it explicitly.
    const scope = searchParams.get('scope');
    if (scope === 'all') next.assignedUserId = undefined;
    else if (scope === 'mine') next.assignedUserId = 'me';
    else if (scope === 'unassigned') next.assignedUserId = 'unassigned';
    // Search term (from global search) — also seed the search box.
    const search = searchParams.get('search');
    if (search) { next.search = search; setSearchInput(search); }
    // Pipeline stage / temperature (from dashboard tiles & charts), validated.
    const stage = searchParams.get('stage');
    if (stage && (VALID_STAGES as readonly string[]).includes(stage)) {
      next.stage = stage as PersonFilters['stage'];
    }
    const temperature = searchParams.get('temperature');
    if (temperature && (VALID_TEMPS as readonly string[]).includes(temperature)) {
      next.temperature = temperature as PersonFilters['temperature'];
    }
    // Segment intent (from the dashboard "Needs attention" deep-links):
    // hot / followUpDue / noActivity30d / inBlast / new.
    const segmentParam = searchParams.get('segment');
    if (segmentParam) setSegment(segmentParam === 'all' ? null : segmentParam);
    if (Object.keys(next).length > 0) setFilters(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync sport / role / state to URL on change
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (sportFilter) next.set('sport', sportFilter); else next.delete('sport');
    if (roleFilter) next.set('role', roleFilter); else next.delete('role');
    if (stateFilter.length) next.set('state', stateFilter.join(',')); else next.delete('state');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sportFilter, roleFilter, stateFilter.join(',')]);

  // Debounced search
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setFilters({ search: value || undefined });
      }, 300);
    },
    [setFilters]
  );

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setFilters({ search: undefined });
  }, [setFilters]);

  const handleSportChange = useCallback(
    (value: string) => {
      setSportInput(value);
      if (sportDebounceRef.current) clearTimeout(sportDebounceRef.current);
      sportDebounceRef.current = setTimeout(() => {
        setFilters({ sport: value.trim() || undefined });
      }, 300);
    },
    [setFilters]
  );

  const handleRoleChange = useCallback(
    (value: string) => {
      setRoleInput(value);
      if (roleDebounceRef.current) clearTimeout(roleDebounceRef.current);
      roleDebounceRef.current = setTimeout(() => {
        setFilters({ role: value.trim() || undefined });
      }, 300);
    },
    [setFilters]
  );

  const handleStateChange = useCallback(
    (value: string) => {
      setStateInput(value);
      if (stateDebounceRef.current) clearTimeout(stateDebounceRef.current);
      stateDebounceRef.current = setTimeout(() => {
        const states = value
          .toUpperCase()
          .split(/[,\s]+/)
          .map((s) => s.trim())
          .filter((s) => s.length === 2);
        setFilters({ state: states.length ? states : undefined });
      }, 300);
    },
    [setFilters]
  );

  const handleClearQueueFilters = useCallback(() => {
    setSportInput('');
    setRoleInput('');
    setStateInput('');
    setFilters({ sport: undefined, role: undefined, state: undefined });
  }, [setFilters]);

  const customerStatusFilter = filters.customerStatus ?? [];
  const hasAnyQueueFilter = Boolean(
    sportFilter || roleFilter || stateFilter.length || customerStatusFilter.length
  );

  const handleCustomerStatusToggle = useCallback(
    (status: CustomerStatus) => {
      const current = filters.customerStatus ?? [];
      const next = current.includes(status)
        ? current.filter((s) => s !== status)
        : [...current, status];
      setFilters({ customerStatus: next.length ? next : undefined });
    },
    [filters.customerStatus, setFilters]
  );

  const handleExcludeActive = useCallback(() => {
    // "Hide current Blast customers" — include every status except ACTIVE.
    setFilters({ customerStatus: ['PROSPECT', 'ONBOARDED', 'SLIPPING', 'LAPSED'] });
  }, [setFilters]);

  const isExcludingActive =
    customerStatusFilter.length === 4 && !customerStatusFilter.includes('ACTIVE');

  const CUSTOMER_STATUS_LABELS: Record<CustomerStatus, string> = {
    PROSPECT: 'Prospect',
    ONBOARDED: 'Onboarded',
    ACTIVE: 'Active customer',
    SLIPPING: 'Slipping',
    LAPSED: 'Lapsed',
  };

  // Segment pill click
  const handleSegmentSelect = useCallback(
    (key: string) => {
      setSegment(key === 'all' ? null : key);
    },
    [setSegment]
  );

  // Build pills array
  const pills = counts
    ? [
        { key: 'all', label: 'All', count: counts.total, Icon: LayoutGrid, iconClass: 'text-gray-500 dark:text-gray-400' },
        { key: 'hot', label: 'Hot', count: counts.hot, Icon: Flame, iconClass: 'text-red-500 dark:text-red-400' },
        { key: 'followUpDue', label: 'Follow Up Due', count: counts.followUpDue, Icon: Clock, iconClass: 'text-amber-500 dark:text-amber-400' },
        { key: 'inBlast', label: 'In Blast', count: counts.inBlast, Icon: Zap, iconClass: 'text-green-600 dark:text-green-400' },
        { key: 'new', label: 'New', count: counts.newThisWeek, Icon: Sparkles, iconClass: 'text-emerald-500 dark:text-emerald-400' },
        { key: 'noActivity30d', label: 'No Activity 30d', count: counts.noActivity30d, Icon: Moon, iconClass: 'text-slate-400 dark:text-slate-500' },
      ]
    : [];

  const activeKey = segment || 'all';

  // Scope label for "Narrow X by" header
  const scopeLabel = useMemo(() => {
    if (filters.assignedUserId === 'me') return 'Mine';
    if (filters.assignedUserId === 'unassigned') return 'Unassigned';
    return 'All';
  }, [filters.assignedUserId]);

  // CRM-16: separate "new rep, nothing assigned" from "filtered to empty". Only
  // the former gets the friendly "switch to All" empty state; an empty result
  // while narrowing filters are active means "no matches", not "nothing yet".
  const isUnfilteredMineEmpty =
    filters.assignedUserId === 'me' && !filters.search && !segment && !hasAnyQueueFilter;

  // Search placeholder reflects active scope (derived from scopeLabel to stay DRY)
  const searchPlaceholder =
    scopeLabel === 'All' ? 'Search all people…' :
    scopeLabel === 'Mine' ? `Search within ${bookLabel(currentUser, 'Mine')}…` :
    'Search Unassigned…';

  // Build counts map for SegmentPills — maps pill keys to in-scope counts from /api/people/segment-counts.
  // The 'all' pill maps to scopeTotal; others map to segmentCounts keys.
  const pillCountsOverride = useMemo<Record<string, number> | undefined>(() => {
    if (!segmentCounts) return undefined;
    return {
      all: segmentCounts.scopeTotal,
      hot: segmentCounts.segmentCounts.hot,
      followUpDue: segmentCounts.segmentCounts.followUpDue,
      inBlast: segmentCounts.segmentCounts.inBlast,
      new: segmentCounts.segmentCounts.new,
      noActivity30d: segmentCounts.segmentCounts.noActivity30d,
    };
  }, [segmentCounts]);

  const currentFilters: QueueFilters = {
    ...(sportFilter && { sport: sportFilter }),
    ...(roleFilter && { role: roleFilter }),
    ...(stateFilter.length > 0 && { state: stateFilter }),
    ...(filters.customerStatus && filters.customerStatus.length > 0 && { customerStatus: filters.customerStatus }),
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        {/* 1. Page header — stacks on mobile, side-by-side from sm: up */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
              <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">People</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage contacts, track activities, and monitor your pipeline
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:flex-shrink-0 w-full sm:w-auto">
            {canMakeQueue && (
              <button
                onClick={() => setShowSaveAsQueue(true)}
                className="btn-primary whitespace-nowrap flex-1 sm:flex-initial"
              >
                <Phone className="h-4 w-4 mr-1.5" />
                Save as Call Queue
              </button>
            )}
            <button
              onClick={() => setIsFormOpen(true)}
              className="btn-primary whitespace-nowrap flex-1 sm:flex-initial"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add Person
            </button>
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-red-800 dark:text-red-300">{error}</span>
            </div>
            <button
              onClick={clearError}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* 2. Scope banner — [All] [Mine] [Unassigned] + in-scope total */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-3" role="group" aria-label="Scope filter">
            <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Show</span>
            <div className="inline-flex rounded-full bg-gray-100 dark:bg-gray-700 p-1">
              {(
                [
                  { value: 'me', label: bookLabel(currentUser, 'Mine') },
                  { value: 'unassigned', label: 'Unassigned' },
                  { value: undefined, label: 'All' },
                ] as { value: string | undefined; label: string }[]
              ).map((opt) => {
                const isActive = (filters.assignedUserId ?? undefined) === opt.value;
                return (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => setFilters({ assignedUserId: opt.value })}
                    aria-pressed={isActive}
                    className={`px-4 py-1.5 rounded-full text-sm transition-colors ${
                      isActive
                        ? 'bg-[#b7ee41] text-gray-900 font-semibold'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex items-baseline gap-2 text-gray-600 dark:text-gray-400 text-sm">
            <span className="text-2xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
              {isLoadingCounts ? '—' : (segmentCounts?.scopeTotal ?? '—')}
            </span>
            <span>people in scope</span>
          </div>
        </div>

        {/* 3. Search bar — placeholder reflects active scope */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="input pl-10 w-full"
          />
          {searchInput && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* 4. Segment chips — "Narrow X by:" label + pills with in-scope counts */}
        {pills.length > 0 && (
          <div>
            <div
              id="segment-pills-label"
              className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2"
            >
              Narrow {scopeLabel} by:
            </div>
            <SegmentPills
              pills={pills}
              activeKey={activeKey}
              onSelect={handleSegmentSelect}
              counts={pillCountsOverride}
              isLoadingCounts={isLoadingCounts}
              aria-labelledby="segment-pills-label"
            />
          </div>
        )}

        {/* 5. Advanced filters — collapsed <details>, auto-expands if any value set at mount */}
        <details
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl"
          open={isAdvancedOpen}
          onToggle={(e) => setIsAdvancedOpen((e.target as HTMLDetailsElement).open)}
        >
          <summary className="cursor-pointer select-none px-4 py-3 text-sm text-gray-700 dark:text-gray-300 font-medium list-none flex items-center justify-between">
            <span>Advanced filters · sport, role, state, status</span>
            {hasAnyQueueFilter && (
              <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium">
                active
              </span>
            )}
          </summary>
          <div className="p-4 pt-0">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                {pagination && !isLoading && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium">
                    {pagination.totalCount.toLocaleString()} {pagination.totalCount === 1 ? 'person' : 'people'} match
                  </span>
                )}
              </div>
              {hasAnyQueueFilter && (
                <button
                  type="button"
                  onClick={handleClearQueueFilters}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                >
                  Clear filters
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Sport
                </span>
                <input
                  type="text"
                  value={sportInput}
                  onChange={(e) => handleSportChange(e.target.value)}
                  placeholder="e.g. football"
                  className="input"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Role / title
                </span>
                <input
                  type="text"
                  value={roleInput}
                  onChange={(e) => handleRoleChange(e.target.value)}
                  placeholder="e.g. coach"
                  className="input"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  States <span className="text-gray-400 font-normal">(2-letter, comma-separated)</span>
                </span>
                <input
                  type="text"
                  value={stateInput}
                  onChange={(e) => handleStateChange(e.target.value)}
                  placeholder="CA, NY"
                  className="input"
                />
              </label>
            </div>

            {/* Customer-status row */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Customer status <span className="text-gray-400 font-normal">— click chips to include only those statuses</span>
                </span>
                <button
                  type="button"
                  onClick={isExcludingActive
                    ? () => setFilters({ customerStatus: undefined })
                    : handleExcludeActive}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {isExcludingActive ? 'Show all statuses' : 'Hide active customers'}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {VALID_STATES.map((status) => {
                  const isOn = customerStatusFilter.includes(status);
                  return (
                    <button
                      key={status}
                      type="button"
                      onClick={() => handleCustomerStatusToggle(status)}
                      aria-pressed={isOn}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        isOn
                          ? 'bg-blue-600 dark:bg-blue-500 border-blue-600 dark:border-blue-500 text-white'
                          : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400 dark:hover:border-blue-500'
                      }`}
                    >
                      {CUSTOMER_STATUS_LABELS[status]}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </details>

        {/* 6. Card list */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
          </div>
        ) : people.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            {isUnfilteredMineEmpty ? (
              <>
                <p className="text-gray-500 dark:text-gray-400">Nothing assigned to you yet.</p>
                <button
                  type="button"
                  onClick={() => setFilters({ assignedUserId: undefined })}
                  className="btn-secondary text-sm mt-4"
                >
                  Show all people
                </button>
              </>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">No people found</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {people.map((person) => (
              <PersonCard key={person.id} person={person} currentUserId={getBookUserId(currentUser) ?? ''} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {(pagination.page - 1) * pagination.limit + 1}
              {' '}-{' '}
              {Math.min(pagination.page * pagination.limit, pagination.totalCount)} of{' '}
              {pagination.totalCount}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(pagination.page - 1)}
                disabled={!pagination.hasPrevPage}
                className="btn-secondary text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(pagination.page + 1)}
                disabled={!pagination.hasNextPage}
                className="btn-secondary text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Create Person Modal */}
        <PersonForm
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSuccess={() => setIsFormOpen(false)}
        />

        <SaveAsQueueModal
          isOpen={showSaveAsQueue}
          onClose={() => setShowSaveAsQueue(false)}
          currentFilters={currentFilters}
          matchCount={pagination?.totalCount ?? 0}
        />
      </div>
    </div>
  );
};
